import { AppCard } from "@lib/components/AppComponents";
import { Empty, Skeleton, Typography } from "antd";
import { UseQueryProps } from "@lib/types";
import { XAICallLogsDashboardResponse } from "@lib/types/xAI";
import { useMemo } from "react";
import styled from "styled-components";

type Props = {
  query?: UseQueryProps<XAICallLogsDashboardResponse>;
  extra?: string | JSX.Element;
  loading?: boolean;
  refetch?: () => void;
};

const SentimentBar = styled.div`
  width: 100%;
  height: 48px;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  margin-top: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const SentimentSegment = styled.div<{ width: number; color: string }>`
  width: ${({ width }) => width}%;
  height: 100%;
  background: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 13px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(255, 255, 255, 0.2);
  }

  &:hover {
    filter: brightness(1.1);
  }
`;

const ObjectionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    padding-left: 8px;
    background: ${({ theme }) => theme.palette.background.secondary};
    margin: 0 -16px;
    padding-left: 24px;
    padding-right: 16px;
    border-radius: 8px;
  }
`;

const ObjectionBar = styled.div<{ width: number; color: string }>`
  height: 28px;
  width: ${({ width }) => width}%;
  background: linear-gradient(
    90deg,
    ${({ color }) => color} 0%,
    ${({ color }) => color}dd 100%
  );
  border-radius: 8px;
  min-width: 20px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px ${({ color }) => color}40;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 8px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.2) 100%
    );
  }
`;

const ObjectionText = styled(Typography.Text)`
  flex: 1;
  margin-right: 20px;
  font-size: 14px;
  font-weight: 500;
`;

const ObjectionCount = styled(Typography.Text)`
  font-weight: 700;
  min-width: 50px;
  text-align: right;
  font-size: 15px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

// Demo data generator
const generateDemoSentimentData = () => {
  return {
    positive: 65,
    neutral: 20,
    negative: 15,
  };
};

const generateDemoObjections = () => {
  return [
    { text: "Spreadler çok geniş", count: 184, color: "#ff4d4f" },
    { text: "Bonus şartları belirsiz", count: 122, color: "#fa8c16" },
    { text: "Lisans endişeleri", count: 95, color: "#fadb14" },
    { text: "Yüksek swap maliyeti", count: 68, color: "#1890ff" },
  ];
};

const TftSalesConversationIntelligence = (props: Props) => {
  const { query, extra, loading } = props;
  const { data, isLoading, isFetching } = query || {};

  // Try to calculate sentiment from real data if available
  const sentimentData = useMemo(() => {
    // TODO: When API provides sentiment aggregation, use real data
    // For now, use demo data
    return generateDemoSentimentData();
  }, [data]);

  const objections = useMemo(() => {
    // TODO: When API provides objection analysis, use real data
    // For now, use demo data
    return generateDemoObjections();
  }, [data]);

  const maxObjectionCount = Math.max(...objections.map((o) => o.count));

  return (
    <AppCard title="Konuşma Zekası" extra={extra} style={{ marginBottom: 16 }}>
      {isLoading || isFetching || loading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <div style={{ padding: "16px" }}>
          {/* Conversation Intelligence Section */}
          <div style={{ marginBottom: 40 }}>
            <Typography.Title
              level={5}
              style={{
                marginBottom: 20,
                fontWeight: 700,
                letterSpacing: "0.5px",
              }}
            >
              KONUŞMA ZEKASI
            </Typography.Title>

            <div style={{ marginBottom: 10 }}>
              <Typography.Text
                style={{
                  color: "#52c41a",
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: "0.3px",
                }}
              >
                POZITIF KONUŞMA {sentimentData.positive}%
              </Typography.Text>
            </div>
            <div style={{ marginBottom: 10 }}>
              <Typography.Text
                style={{
                  color: "#8c8c8c",
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: "0.3px",
                }}
              >
                NÖTR {sentimentData.neutral}%
              </Typography.Text>
            </div>
            <div style={{ marginBottom: 10 }}>
              <Typography.Text
                style={{
                  color: "#ff4d4f",
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: "0.3px",
                }}
              >
                NEGATIF KONUŞMA {sentimentData.negative}%
              </Typography.Text>
            </div>

            <SentimentBar>
              <SentimentSegment width={sentimentData.positive} color="#52c41a">
                {sentimentData.positive}%
              </SentimentSegment>
              <SentimentSegment width={sentimentData.neutral} color="#d9d9d9">
                {sentimentData.neutral}%
              </SentimentSegment>
              <SentimentSegment width={sentimentData.negative} color="#ff4d4f">
                {sentimentData.negative}%
              </SentimentSegment>
            </SentimentBar>
          </div>

          {/* Top Objection Analysis Section */}
          <div>
            <Typography.Title
              level={5}
              style={{
                marginBottom: 20,
                fontWeight: 700,
                letterSpacing: "0.5px",
              }}
            >
              TOP OBJECTION ANALYSIS
            </Typography.Title>

            {objections.length === 0 ? (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <Empty />
              </div>
            ) : (
              <div>
                {objections.map((objection, index) => {
                  const widthPercent =
                    (objection.count / maxObjectionCount) * 100;
                  return (
                    <ObjectionItem key={index}>
                      <ObjectionText>{objection.text}</ObjectionText>
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 16,
                          marginRight: 16,
                        }}
                      >
                        <ObjectionBar
                          width={widthPercent}
                          color={objection.color}
                        />
                      </div>
                      <ObjectionCount>{objection.count}</ObjectionCount>
                    </ObjectionItem>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </AppCard>
  );
};

export default TftSalesConversationIntelligence;
